import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Box, IconButton, InputAdornment, TextField as MuiTextField } from '@mui/material';
import cn from 'classnames';
import React, { forwardRef, useEffect, useState } from 'react';
import { RegisterOptions, UseFormRegister } from 'react-hook-form';

import styles from './styles.module.scss';

interface InputProps {
  wrapperClassName?: string;
  inputClass?: string;
  register?: UseFormRegister<any>;
  registerOptions?: RegisterOptions;
  errors?: { [key: string]: any };
  wrapperStyle?: React.CSSProperties;
  type?: string;
  name: string;
  label?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  disabled?: boolean;
  maxLength?: number;
  autoComplete?: string;
  value?: any;
  required?: boolean;
  showPasswordToggle?: boolean; // Enable eye toggle for password
  multiline?: boolean | undefined;
  minRows?: string | number | undefined;
  maxRows?: string | number | undefined;
}

const CustomTextField = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      wrapperClassName,
      inputClass,
      wrapperStyle,
      name,
      register,
      registerOptions,
      errors,
      type = 'text',
      label,
      onChange,
      onBlur,
      onFocus,
      disabled = false,
      maxLength = 255,
      autoComplete,
      value,
      required,
      showPasswordToggle = false,
      multiline,
      minRows,
      maxRows,
      ...props
    },
    ref,
  ) => {
    const [errorMessage, setErrorMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
      if (errors && name) {
        const error = name.split('.').reduce((acc, key) => acc?.[key], errors)?.message;
        setErrorMessage(error || '');
      } else {
        setErrorMessage('');
      }
    }, [errors, name]);

    return (
      <Box className={cn(styles.inputWrapper, wrapperClassName)} style={wrapperStyle}>
        <MuiTextField
          multiline={multiline ?? undefined}
          minRows={minRows ?? undefined}
          maxRows={maxRows ?? undefined}
          className={cn(styles.input, inputClass)}
          {...(register ? register(name, registerOptions) : {})}
          InputLabelProps={{
            shrink: Boolean(value) || false,
            sx: {
              fontSize: '14px',
              backgroundColor: 'white',
              px: 0.3,
              '&.MuiInputLabel-shrink': {
                transform: 'translate(6px, -10px) scale(0.75)',
              },
              '&::after': required
                ? {
                    content: '" *"',
                    color: 'red',
                    opacity: 0.5,
                    fontSize: '16px',
                    fontWeight: 'bold',
                  }
                : {},
            },
          }}
          sx={{
            '& .MuiOutlinedInput-notchedOutline legend': { maxWidth: '0.01px !important' },
            '& .MuiInputLabel-shrink + .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline legend': {
              maxWidth: '0.01px !important',
            },
          }}
          type={showPasswordToggle && showPassword ? 'text' : type}
          ref={ref}
          fullWidth
          variant="outlined"
          label={label}
          disabled={disabled}
          inputProps={{ maxLength }}
          error={!!errorMessage}
          helperText={errorMessage}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          autoComplete={autoComplete}
          value={value}
          {...props}
          InputProps={{
            endAdornment: showPasswordToggle ? (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>
    );
  },
);

export default CustomTextField;
